import React, { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import ChipList from '@components-clean/core/chip-list';
import { datasetFilterValues } from '@components-clean/core/comparison-dataset-card/comparison-dataset-card.data';
import EditCard from '@components-clean/core/edit-card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ADD_STUDY_DASHBOARD_CONFIG, GET_STUDY_DASHBOARD_CONFIGURATION } from '@queries/central-monitoring';
import {
    studyDashboardConfigForStudy,
    studyDashboardConfigForStudyVariables,
    studyDashboardConfigForStudy_OpraQueries_studyDashboardConfigForStudy_StudyDashboardConfiguration as DashboardConfig,
    addStudyDashboardConfig,
    addStudyDashboardConfigVariables,
} from '@queries/generated/central-monitoring.generated';

const COMPONENT_NAME = 'comparisonDataset';

interface ComparisonDatasetCardProps {
    studyRef: string | null;
    canEdit: boolean;
}
const ComparisonDatasetCard = ({ studyRef, canEdit }: ComparisonDatasetCardProps) => {
    const { t } = useTranslation();
    const isEditMode = useBoolean(false);
    const { confirmDiscardChanges, setComponentHasChanges } = useUnsavedChangesContext();

    const [loadConfig, { data: studyConfigData, loading: studyConfigLoading, error: dataError }] = useLazyQuery<
        studyDashboardConfigForStudy,
        studyDashboardConfigForStudyVariables
    >(GET_STUDY_DASHBOARD_CONFIGURATION);

    const [saveConfig, { loading: isMutatingStudyConfigData, error: mutationError }] = useMutation<
        addStudyDashboardConfig,
        addStudyDashboardConfigVariables
    >(ADD_STUDY_DASHBOARD_CONFIG);

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty, isValid },
    } = useForm<DashboardConfig>({
        mode: 'all',
        defaultValues: {},
    });

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
    }, [studyRef]);

    useEffect(() => {
        if (!isEditMode.value) {
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isEditMode.value]);

    useEffect(() => {
        if (studyConfigData) {
            const dataSetsCount = studyConfigData.studyDashboardConfigForStudy?.dataSetsCount;
            const dataSetFilter = studyConfigData.studyDashboardConfigForStudy?.dataSetFilter;
            reset({ dataSetFilter, dataSetsCount }, { keepDirty: false });
        }
    }, [studyConfigData]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty]);

    const onSubmit: SubmitHandler<DashboardConfig> = (data: DashboardConfig) => {
        if (isDirty) {
            saveConfig({
                variables: {
                    studyId: studyRef,
                    studyDashboardConfig: {
                        id: studyConfigData?.studyDashboardConfigForStudy?.id as string,
                        dataSetsCount: data.dataSetsCount,
                        dataSetFilter: data.dataSetFilter,
                    },
                },
            })
                .then(() => {
                    isEditMode.setFalse();
                    ShowSuccess(t('actionsSuccessfulToast'));
                })
                .catch(() => {
                    ShowError(t('errorMessage'));
                });
        } else {
            isEditMode.setFalse();
        }
    };

    const isLoading = !studyConfigData || studyConfigLoading || isMutatingStudyConfigData;
    const isError = dataError || mutationError;
    return (
        <EditCard
            title={t('admin:datasetSelector')}
            onEdit={() => isEditMode.setTrue()}
            onCancel={async () => {
                if (isDirty) {
                    const ok = await confirmDiscardChanges('confirmation-dialog');
                    if (!ok) {
                        return;
                    }
                }
                reset({}, { keepDirty: false });
                isEditMode.setFalse();
            }}
            onSave={handleSubmit(onSubmit)}
            isEditMode={isEditMode.value}
            disabled={!isValid || isLoading || !canEdit}
            data-testid="comparison-dataset"
        >
            {isLoading && !isError && <Loading />}
            {isError && <ErrorMessage />}
            {!isLoading && !isError && (
                <>
                    <div className="k-display-flex k-flex-nowrap k-align-items-center k-mb-5">
                        <div style={{ minWidth: 425 }}>
                            <span
                                className="k-d-inline-block k-mr-4 k-w-full k-font-size-md k-color-primary"
                                style={{ maxWidth: 130 }}
                            >
                                {t('study:datasetFilter')}
                            </span>
                            <Controller
                                control={control}
                                name="dataSetFilter"
                                render={({ field: { onChange, value } }) => (
                                    <ChipList
                                        // We use a compound key here to prevent a bug with the focus state of the chiplist causing
                                        // some rerenders to steal focus from input elements
                                        key={String(isDirty)}
                                        data-testid="chip-list"
                                        disabled={!isEditMode.value}
                                        value={String(value)} // Kendo needs value as string
                                        onChange={(e) => onChange(Number(e.value))} // Convert to number for BFF
                                        defaultData={datasetFilterValues}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="k-display-flex k-flex-nowrap k-align-items-center k-mb-5">
                        <div className="k-display-flex k-flex-nowrap k-align-items-center" style={{ minWidth: 425 }}>
                            <span
                                className="k-d-inline-block k-mr-4 k-w-full k-font-size-md k-color-primary"
                                style={{ maxWidth: 130 }}
                            >
                                {t('study:datasetsToDisplay')}
                            </span>
                            <NumericTextBoxController
                                control={control}
                                name="dataSetsCount"
                                required
                                data-testid="dataset-numeric-text-box"
                                disabled={!isEditMode.value}
                                format="#"
                                min={0}
                                max={12}
                                style={{ width: 210 }}
                            />
                        </div>
                    </div>
                </>
            )}
        </EditCard>
    );
};

export default ComparisonDatasetCard;
